import { APITypesV1 } from "@cur8/api-client";
import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import { useRef, useMemo, useState } from "react";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { getSafeReturnPath } from "lib/getSafeReturnPath";
import { CheckInParams } from "lib/types/checkInParameters";
import { useLocation } from "@pomle/react-router-paths";
import { paths } from "render/routes/paths";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { CheckCard } from "render/ui/presentation/CheckCard";
import { Group } from "render/ui/presentation/Group";
import { Radio, RadioGroup } from "react-aria-components";
import { BailView } from "render/views/BailView";
import styles from "./styles.module.sass";

type IdentityCheckViewProps = {
  goTo(url: string): void;
  onBack(): void;
} & CheckInParams;

export function IdentityCheckView({
  goTo,
  onBack,
  patientId,
  visitId,
}: IdentityCheckViewProps) {
  const { data: patient } = usePatientQuery({ patientId });
  const { data: visit } = useVisitQuery({ patientId, visitId });
  const [identityConfirmed, setIdentityConfirmed] = useState(false);
  const [selectedDobIndex, setSelectedDobIndex] = useState("");
  const [showDobError, setShowDobError] = useState(false);
  const location = useLocation();

  const isBodyScan = visit?.medicalExams?.includes(
    APITypesV1.MedicalExam.NekoBodyScan
  );

  const nextUrl = useMemo(() => {
    const fallback = isBodyScan
      ? paths.preferredName.url({ patientId, visitId })
      : paths.assignToRoom.url({ patientId, visitId });
    return getSafeReturnPath(location.searchParams.get("returnPath"), fallback);
  }, [isBodyScan, location.searchParams, patientId, visitId]);

  const dobOptionsRef = useRef<{
    key: string | null;
    options: Array<{ label: string; isCorrect: boolean }> | null;
  }>({ key: null, options: null });

  const dobKey = patient?.dateOfBirth?.toISODate() ?? null;
  if (dobOptionsRef.current.key !== dobKey) {
    const dob = patient?.dateOfBirth ?? null;
    let options: Array<{ label: string; isCorrect: boolean }> | null = null;
    if (dob && dobKey) {
      const correctLabel = dob.toFormat("d MMMM yyyy");
      const usedLabels = new Set([correctLabel]);

      const generateDistractor = () => {
        let label: string;
        do {
          const yearOffset =
            (Math.floor(Math.random() * 5) + 1) *
            (Math.random() < 0.5 ? -1 : 1);
          label = dob
            .set({
              year: dob.year + yearOffset,
              month: Math.floor(Math.random() * 12) + 1,
              day: Math.floor(Math.random() * 28) + 1,
            })
            .toFormat("d MMMM yyyy");
        } while (usedLabels.has(label));
        usedLabels.add(label);
        return label;
      };

      options = [
        { label: generateDistractor(), isCorrect: false },
        { label: correctLabel, isCorrect: true },
        { label: generateDistractor(), isCorrect: false },
      ];

      for (let i = options.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [options[i], options[j]] = [options[j], options[i]];
      }
    }
    dobOptionsRef.current = { key: dobKey, options };
  }

  const dobOptions = dobOptionsRef.current.options;

  const fullName =
    [patient?.name?.firstName, patient?.name?.lastName]
      .filter(Boolean)
      .join(" ") ||
    patient?.name?.displayName ||
    "the member";

  const dateOfBirth = patient?.dateOfBirth?.toFormat("d MMMM yyyy");
  const physicalId = patient?.physicalIds?.[0];
  const physicalIdDisplay =
    physicalId != null
      ? typeof physicalId === "string"
        ? physicalId
        : physicalId.id
      : undefined;
  const countryCode = visit?.site.address.countryCode?.toUpperCase();
  const idLabel =
    countryCode === "SE"
      ? "Personnummer"
      : countryCode === "GB"
        ? "NHS number"
        : "Personal ID";

  if (!patient) {
    return <BailView title="Could not fetch member" />;
  }

  const canContinue = dobOptions
    ? identityConfirmed && selectedDobIndex !== ""
    : identityConfirmed;

  function handleBack() {
    setShowDobError(false);
    setSelectedDobIndex("");
    onBack();
  }

  function handleContinue() {
    if (dobOptions) {
      const selected = dobOptions[parseInt(selectedDobIndex)];
      if (!selected?.isCorrect) {
        setShowDobError(true);
        return;
      }
    }
    goTo(nextUrl);
  }

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <>
            <div>
              <Typography variant="display-s">Confirm identity</Typography>
              <Typography variant="body-m" color="subtle">
                Verify the member&apos;s identity before continuing.
              </Typography>
            </div>
            {dobOptions ? (
              <>
                <Group highlight={identityConfirmed ? "blue" : "none"}>
                  <CheckCard
                    aria-label="Confirm identity"
                    isSelected={identityConfirmed}
                    onChange={setIdentityConfirmed}
                  >
                    <div className={styles.cardBody}>
                      <div className={styles.detailRow}>
                        <Typography variant="label-s" color="subtle">
                          Name
                        </Typography>
                        <Typography variant="body-m">{fullName}</Typography>
                      </div>
                      {physicalIdDisplay && (
                        <div className={styles.detailRow}>
                          <Typography variant="label-s" color="subtle">
                            {idLabel}
                          </Typography>
                          <Typography variant="body-m">
                            {physicalIdDisplay}
                          </Typography>
                        </div>
                      )}
                    </div>
                  </CheckCard>
                </Group>
                <div className={styles.dobChallenge}>
                  <div>
                    <Typography variant="label-m">Date of birth</Typography>
                    <Typography variant="body-s" color="subtle">
                      Ask the member, then select the matching date
                    </Typography>
                  </div>
                  <RadioGroup
                    value={selectedDobIndex}
                    onChange={(v) => {
                      setSelectedDobIndex(v);
                      setShowDobError(false);
                    }}
                    className={styles.dobRadioGroup}
                  >
                    {dobOptions.map((opt, i) => (
                      <Radio
                        key={opt.label}
                        value={String(i)}
                        className={styles.dobRadio}
                      >
                        <div className={styles.dobRadioIndicator} />
                        <Typography variant="body-m">{opt.label}</Typography>
                      </Radio>
                    ))}
                  </RadioGroup>
                  {showDobError && (
                    <div className={styles.dobError}>
                      <Typography variant="body-s">
                        This does not match our records. Please check again.
                      </Typography>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Group highlight={identityConfirmed ? "blue" : "none"}>
                <CheckCard
                  aria-label="Confirm identity"
                  isSelected={identityConfirmed}
                  onChange={setIdentityConfirmed}
                >
                  <div className={styles.cardBody}>
                    <Typography variant="eyebrow-m" color="subtle">
                      Member
                    </Typography>
                    <div className={styles.detailRow}>
                      <Typography variant="label-s" color="subtle">
                        Name
                      </Typography>
                      <Typography variant="body-m">{fullName}</Typography>
                    </div>
                    {dateOfBirth && (
                      <div className={styles.detailRow}>
                        <Typography variant="label-s" color="subtle">
                          Date of birth
                        </Typography>
                        <Typography variant="body-m">{dateOfBirth}</Typography>
                      </div>
                    )}
                    {physicalIdDisplay && (
                      <div className={styles.detailRow}>
                        <Typography variant="label-s" color="subtle">
                          {idLabel}
                        </Typography>
                        <Typography variant="body-m">
                          {physicalIdDisplay}
                        </Typography>
                      </div>
                    )}
                  </div>
                </CheckCard>
              </Group>
            )}
          </>
        }
        buttons={
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={handleBack} fillContainer>
                Back
              </Button>
            }
            right={
              <Button
                state={!canContinue ? "disabled" : undefined}
                onPress={handleContinue}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                Continue
              </Button>
            }
          />
        }
      />
    </PageFrameContent>
  );
}
